import { useState, useEffect, useRef, useCallback } from 'react';
import {
  BookOpenIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PageHero from '../components/ui/PageHero';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import knowledgeApi, {
  KnowledgeSource,
  KnowledgeStats,
  KnowledgeSearchResult,
  KnowledgeSourceType,
} from '../api/knowledge';

const SOURCE_TYPES: KnowledgeSourceType[] = [
  'book',
  'article',
  'manual',
  'research',
  'notes',
  'text',
  'url',
];

const SUGGESTED_CATEGORIES = [
  'Nutrition',
  'Breeding',
  'Diseases & Health',
  'Husbandry',
  'Species Reference',
  'Genetics',
  'Incubation',
  'General',
];

function formatBytes(bytes?: number | null): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

function StatusBadge({ status }: { status: KnowledgeSource['status'] }) {
  const map = {
    ready: { cls: 'bg-green-100 text-green-700', icon: CheckCircleIcon, label: 'Ready' },
    processing: { cls: 'bg-amber-100 text-amber-700', icon: ClockIcon, label: 'Processing' },
    failed: { cls: 'bg-red-100 text-red-700', icon: ExclamationTriangleIcon, label: 'Failed' },
  } as const;
  const cfg = map[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

export default function KnowledgeLibrary() {
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [stats, setStats] = useState<KnowledgeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  // Retrieval test
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<KnowledgeSearchResult[] | null>(null);
  const [testing, setTesting] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (bustCache = false) => {
    try {
      const [list, st] = await Promise.all([
        knowledgeApi.list({ bustCache }),
        knowledgeApi.stats(),
      ]);
      setSources(list);
      setStats(st);
    } catch (error: any) {
      console.error('Failed to load knowledge library', error);
      toast.error('Failed to load knowledge library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while anything is processing.
  useEffect(() => {
    const hasProcessing = sources.some((s) => s.status === 'processing');
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(() => load(true), 4000);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [sources, load]);

  const handleDelete = async (source: KnowledgeSource) => {
    if (!window.confirm(`Delete "${source.title}" from the knowledge library?`)) return;
    try {
      await knowledgeApi.remove(source.id);
      toast.success('Source deleted');
      load(true);
    } catch {
      toast.error('Failed to delete source');
    }
  };

  const handleReindex = async (source: KnowledgeSource) => {
    try {
      await knowledgeApi.reindex(source.id);
      toast.success('Re-indexing started');
      load(true);
    } catch {
      toast.error('Failed to re-index source');
    }
  };

  const handleTest = async () => {
    if (!testQuery.trim()) return;
    setTesting(true);
    try {
      const results = await knowledgeApi.search(testQuery.trim(), 6);
      setTestResults(results);
      if (results.length === 0) {
        toast('No matching passages found', { icon: '🔎' });
      }
    } catch {
      toast.error('Search failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <PageHero
        title="Knowledge Library"
        subtitle="Ingest books, manuals and research so BirdChatter can augment and validate your BirdMan data"
        actions={
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
          >
            <PlusIcon className="h-5 w-5" />
            Add Source
          </button>
        }
      />

      {/* Embeddings warning */}
      {stats && !stats.embeddingsEnabled && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
          <span className="text-sm text-yellow-800">
            OPENAI_API_KEY is not configured. Sources can be added but cannot be indexed for semantic search until a key is set.
          </span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard label="Sources" value={stats.totalSources} />
          <StatCard label="Ready" value={stats.readySources} tone="green" />
          <StatCard label="Processing" value={stats.processingSources} tone="amber" />
          <StatCard label="Indexed Chunks" value={stats.totalChunks} />
          <StatCard label="Characters" value={stats.totalCharacters.toLocaleString()} />
        </div>
      )}

      {/* Retrieval test */}
      <div className="mt-8 rounded-lg bg-white p-4 shadow sm:p-6">
        <h3 className="mb-3 text-lg font-medium text-slate-900">Test Retrieval</h3>
        <p className="mb-3 text-sm text-slate-500">
          Ask a question to preview which passages BirdChatter would pull from your library.
        </p>
        <div className="flex gap-2">
          <input
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleTest()}
            placeholder="e.g. What is the ideal protein level for breeding African Greys?"
            className="input flex-1"
          />
          <button
            onClick={handleTest}
            disabled={testing || !testQuery.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            {testing ? 'Searching…' : 'Search'}
          </button>
        </div>

        {testResults && testResults.length > 0 && (
          <div className="mt-4 space-y-3">
            {testResults.map((r) => (
              <div key={r.chunkId} className="rounded-lg border border-slate-200 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">
                    {r.sourceTitle}
                    {r.author ? ` — ${r.author}` : ''}
                  </span>
                  <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">
                    {(r.score * 100).toFixed(0)}% match
                  </span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-4 whitespace-pre-wrap">{r.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sources list */}
      <div className="mt-8 overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-6">
          <h3 className="text-lg font-medium text-slate-900">Sources</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-cyan-500" />
          </div>
        ) : sources.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <BookOpenIcon className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">Your library is empty</p>
            <p className="mt-1 text-sm text-slate-500">
              Add a book, manual, or notes to start building your intelligent reference base.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sources.map((source) => (
              <li key={source.id} className="flex items-start justify-between gap-4 px-4 py-4 sm:px-6">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{source.title}</span>
                    <StatusBadge status={source.status} />
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs capitalize text-slate-600">
                      {source.sourceType}
                    </span>
                    {source.category && (
                      <span className="rounded bg-cyan-50 px-1.5 py-0.5 text-xs text-cyan-700">
                        {source.category}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {source.author && <span>by {source.author} · </span>}
                    {source.status === 'ready' && (
                      <span>{source.chunkCount} chunks · {source.characterCount.toLocaleString()} chars</span>
                    )}
                    {source.fileName && <span> · {source.fileName} ({formatBytes(source.fileSize)})</span>}
                  </div>
                  {source.status === 'failed' && source.errorMessage && (
                    <p className="mt-1 text-xs text-red-600">{source.errorMessage}</p>
                  )}
                  {source.description && (
                    <p className="mt-1 text-sm text-slate-600 line-clamp-2">{source.description}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => handleReindex(source)}
                    title="Re-index"
                    className="rounded p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(source)}
                    title="Delete"
                    className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAdd && (
        <AddSourceModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load(true);
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'green' | 'amber';
}) {
  const valueCls =
    tone === 'green' ? 'text-green-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="rounded-lg bg-white p-4 shadow">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${valueCls}`}>{value}</p>
    </div>
  );
}

function AddSourceModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<'file' | 'text'>('file');
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('');
  const [sourceType, setSourceType] = useState<KnowledgeSourceType>('book');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async () => {
    const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);

    if (mode === 'file') {
      if (!file) {
        toast.error('Please choose a file');
        return;
      }
      setSubmitting(true);
      try {
        await knowledgeApi.upload(file, {
          title: title.trim() || file.name,
          author: author.trim() || undefined,
          category: category.trim() || undefined,
          description: description.trim() || undefined,
          tags: tagList,
        });
        toast.success('File uploaded — indexing in background');
        onSaved();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Upload failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSubmitting(true);
    try {
      await knowledgeApi.createText({
        title: title.trim(),
        content: content.trim(),
        author: author.trim() || undefined,
        category: category.trim() || undefined,
        sourceType,
        description: description.trim() || undefined,
        tags: tagList,
      });
      toast.success('Source added — indexing in background');
      onSaved();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to add source');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BrandModal open onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Add Knowledge Source" onClose={onClose} />
      <BrandModalBody>
        {/* Mode toggle */}
        <div className="mb-4 inline-flex rounded-lg border border-slate-200 p-1">
          <button
            type="button"
            onClick={() => setMode('file')}
            className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium ${
              mode === 'file' ? 'bg-cyan-600 text-white' : 'text-slate-600'
            }`}
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setMode('text')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              mode === 'text' ? 'bg-cyan-600 text-white' : 'text-slate-600'
            }`}
          >
            Paste Text
          </button>
        </div>

        <div className="space-y-4">
          {mode === 'file' ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">File</label>
              <input
                type="file"
                accept=".pdf,.txt,.md,.csv,.json,.html,.htm"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
                }}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-cyan-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-cyan-700"
              />
              <p className="mt-1 text-xs text-slate-400">Supported: PDF, TXT, Markdown, CSV, JSON, HTML (max 50MB)</p>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                placeholder="Paste the reference text / notes here…"
                className="input w-full"
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Title *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Author</label>
              <input value={author} onChange={(e) => setAuthor(e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Category</label>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                list="kb-categories"
                className="input w-full"
                placeholder="e.g. Nutrition"
              />
              <datalist id="kb-categories">
                {SUGGESTED_CATEGORIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Type</label>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as KnowledgeSourceType)}
                className="input w-full capitalize"
              >
                {SOURCE_TYPES.map((t) => (
                  <option key={t} value={t} className="capitalize">
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Tags (comma separated)</label>
            <input value={tags} onChange={(e) => setTags(e.target.value)} className="input w-full" />
          </div>
        </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="btn btn-primary text-sm"
        >
          {submitting ? 'Saving…' : 'Add to Library'}
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}
